import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Heart, ShoppingBag, Check } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { optimizedImg, srcsetFor } from "@/lib/imgOptimize";

export default function ProductCard({ product }) {
  const { lang, format, t, toggleWishlist, inWishlist, user, addToCart } = useApp();
  // Language cascade: preferred lang → English → German → slug (avoids German fallback in unsupported locales)
  const name = product.name?.[lang] || product.name?.en || product.name?.de || product.slug;
  const catKey = "cat_" + (product.category || "").replace(/-/g, "_");
  const catLabel = (() => { const v = t(catKey); return v && v !== catKey ? v : (product.category || "").replace(/-/g," "); })();
  const sale = product.sale_price_chf && product.sale_price_chf < product.price_chf;
  // Read the sanitized stock_state the backend now sends. Fallback to raw stock only for legacy caches.
  const outOfStock = product.stock_state
    ? (product.stock_state.state === "out" || product.stock_state.state === "setup")
    : Number(product.stock ?? 0) <= 0;
  const [added, setAdded] = useState(false);

  const onHeart = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { toast.info(t("wishlist_login")); return; }
    const res = await toggleWishlist(product.id);
    if (res?.needLogin) toast.info(t("wishlist_login"));
    else toast.success(res.in_wishlist ? t("wishlist_add") : t("wishlist_remove"));
  };

  // Quick-add: fires without opening the detail page. If the product has size/variant
  // requirements we still bounce the customer to the detail page (safer than picking a
  // default). Otherwise we drop it straight into the cart drawer.
  const onQuickAdd = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (outOfStock) { toast.error(t("out_of_stock")); return; }
    if ((product.sizes && product.sizes.length > 1) || (product.variants && product.variants.length > 1)) {
      // Let the user choose on the detail page.
      window.location.href = `/product/${product.slug}`;
      return;
    }
    addToCart(product, 1, product.sizes?.[0] || null, product.variants?.[0] || null);
    setAdded(true);
    setTimeout(() => setAdded(false), 1400);
  };

  return (
    <div data-testid={`product-card-${product.slug}`} className="card-product group relative overflow-hidden">
      <Link to={`/product/${product.slug}`} className="block">
        <div className="relative aspect-[4/5] overflow-hidden bg-black">
          {product.images?.[0] && (
            <img
              loading="lazy"
              decoding="async"
              width="400"
              height="500"
              src={optimizedImg(product.images[0], 400)}
              srcSet={srcsetFor(product.images[0], [200, 400, 800])}
              sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 300px"
              alt={name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          )}
          <div className="absolute top-3 start-3 flex flex-col gap-2">
            {product.is_new && <span className="bg-gold text-black text-[10px] tracking-[0.2em] uppercase px-2 py-1">{t("new_badge")}</span>}
            {product.bestseller && <span className="bg-black/70 border border-gold text-gold text-[10px] tracking-[0.2em] uppercase px-2 py-1">{t("bestseller_badge")}</span>}
            {sale && <span className="bg-red-500 text-white text-[10px] tracking-[0.2em] uppercase px-2 py-1">{t("sale_badge")}</span>}
          </div>
          <button onClick={onHeart} aria-label={t("aria_wishlist")} data-testid={`wishlist-btn-${product.slug}`}
            className={`absolute top-3 end-3 w-9 h-9 flex items-center justify-center rounded-full backdrop-blur-md border transition-colors ${inWishlist(product.id) ? "bg-gold border-gold text-black" : "bg-black/40 border-white/20 text-white hover:border-gold hover:text-gold"}`}>
            <Heart size={16} fill={inWishlist(product.id) ? "currentColor" : "none"} strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-4 sm:p-5 pb-3">
          <div className="text-[10px] tracking-[0.25em] uppercase text-white/40 mb-2">{catLabel}</div>
          <h3 className="text-white text-base leading-snug min-h-[3rem]">{name}</h3>
          <div className="mt-3 flex items-center justify-between">
            <span className="text-gold font-serif-display text-lg">
              {sale ? (
                <><span className="text-white/40 line-through me-2 text-sm">{format(product.price_chf)}</span>{format(product.sale_price_chf)}</>
              ) : format(product.price_chf)}
            </span>
          </div>
        </div>
      </Link>
      {/* Quick-add button — kept OUTSIDE the Link so it doesn't navigate. */}
      <div className="px-4 sm:px-5 pb-4">
        <button onClick={onQuickAdd} disabled={outOfStock || added}
          data-testid={`quick-add-${product.slug}`}
          className={`w-full inline-flex items-center justify-center gap-2 py-2.5 text-[11px] tracking-[0.2em] uppercase border transition-all ${
            outOfStock ? "border-white/10 text-white/30 cursor-not-allowed" :
            added ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" :
            "border-gold text-gold hover:bg-gold hover:text-black"
          }`}>
          {added ? <><Check size={14}/> {t("cart_added") || "Hinzugefügt"}</> :
           outOfStock ? t("out_of_stock") :
           <><ShoppingBag size={14}/> {t("add_to_cart")}</>}
        </button>
      </div>
    </div>
  );
}
